import { type FC } from 'react'
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router'

import brokersOperationsBatches from 'rails_routes/Brokers/OperationsBatchesPaths'
import { Breadcrumbs } from 'slab'
import { useGetBrokersOperationsBatchesBatchId } from 'utils/web_api'

import OperationsBatchDetail from './OperationsBatchDetail'
import OperationsBatchesList from './OperationsBatchesList'

const OperationsBatchesBreadcrumbs: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // Extract batch ID from path if present
  const pathSegments = location.pathname.split('/')
  const batchId = pathSegments[3] // /brokers/operations_batches/:id

  const isBatchDetails = batchId && pathSegments.length > 3

  // Fetch batch data if we're on a batch details page
  const shouldFetchBatch = Boolean(isBatchDetails && batchId)
  const { data: batch, isLoading: isBatchLoading } = useGetBrokersOperationsBatchesBatchId(
    batchId || '',
    {
      query: {
        enabled: shouldFetchBatch,
      },
    }
  )

  // Generate the batch breadcrumb text or skeleton
  const getBatchBreadcrumbContent = () => {
    // Show skeleton while loading
    if (isBatchLoading) {
      return (
        <div className='flex items-center space-x-1'>
          <div className='h-4 w-24 bg-gray-200 rounded animate-pulse' />
        </div>
      )
    }

    // Show formatted text if data is available
    if (batch?.display_name && batch?.period_start_date) {
      const date = new Date(batch.period_start_date)
      const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      return `${batch.display_name} (${monthYear})`
    }

    return `Batch ${batchId}`
  }

  return (
    <div className='max-w-[2400px] mx-auto px-4 pb-2'>
      <Breadcrumbs>
        <Breadcrumbs.Item>Brokers</Breadcrumbs.Item>
        {isBatchDetails
          ? (
            <>
              <Breadcrumbs.Item onClick={() => navigate(brokersOperationsBatches.index())}>
                Operations Batches
              </Breadcrumbs.Item>
              <Breadcrumbs.Item current>
                {getBatchBreadcrumbContent()}
              </Breadcrumbs.Item>
            </>
            )
          : (
            <Breadcrumbs.Item current>Operations Batches</Breadcrumbs.Item>
            )}
      </Breadcrumbs>
    </div>
  )
}

const OperationsBatchesView: FC = () => {
  return (
    <BrowserRouter>
      <div className='min-h-screen'>
        <OperationsBatchesBreadcrumbs />
        <Routes>
          {/* Main operations batches list view */}
          <Route
            path={brokersOperationsBatches.index()}
            element={<OperationsBatchesList />}
          />
          {/* Individual batch details */}
          <Route
            path='/brokers/operations_batches/:id'
            element={<OperationsBatchDetail />}
          />
        </Routes>
      </div>
    </BrowserRouter>
  )
}

export default OperationsBatchesView
